import React, { useState } from 'react';
import { useLiveData } from '../hooks/useLiveData';
import { getAgentLogs, triggerAgent, isConfigured } from '../lib/api';
import { agents as mockAgents } from '../data/mockData';

const AGENT_DEFS = [
  { id: 'chief_of_staff',   name: 'Chief of Staff',       dept: 'Command',            platform: 'both',     repo: 'meduvita-agents', agentKey: 'chief_of_staff',   brain: 'Gemini 2.5 Pro',   trigger: 'Daily 7:55 AM',    description: 'Aggregates all agent outputs. Writes the Morning Pulse.' },
  { id: 'medical_guardian', name: 'Medical Guardian',     dept: 'Product & Engineering', platform: 'meduvita', repo: 'meduvita-agents', agentKey: 'medical_guardian', brain: 'Gemini 2.0 Pro',   trigger: 'Every 15 mins',    description: 'QA scans every new UCAT/USMLE question against 2026 NICE guidelines.' },
  { id: 'retentionist',     name: 'Retentionist',         dept: 'Customer Love',      platform: 'meduvita', repo: 'meduvita-agents', agentKey: 'retentionist',     brain: 'Gemini 2.5 Flash', trigger: 'Daily midnight',   description: 'Manages 3-7-10-14 day cool nudge email sequences.' },
  { id: 'early_warning_scout', name: 'Early Warning Scout', dept: 'Customer Love',   platform: 'meduvita', repo: 'meduvita-agents', agentKey: 'early_warning_scout', brain: 'Gemini 2.5 Flash', trigger: 'Daily 11 PM',   description: 'Detects quit-risk students and sends personalised rescue emails.' },
  { id: 'matchmaker',       name: 'Matchmaker',           dept: 'Study Engine',       platform: 'meduvita', repo: 'meduvita-agents', agentKey: 'matchmaker',       brain: 'Supabase Logic',   trigger: 'Sunday 9 AM',      description: 'Groups struggling students into Study Rooms by topic.' },
  { id: 'marketing_scout',  name: 'Marketing Scout',      dept: 'Marketing & Growth', platform: 'mmf',      repo: 'mmf-agents',      agentKey: 'marketing_scout',  brain: 'Gemini 2.5 Flash', trigger: 'Daily 8 AM',       description: 'Scrapes competitor pricing, writes daily Reel script.' },
  { id: 'ambassador_boss',  name: 'Ambassador Boss',      dept: 'Marketing & Growth', platform: 'mmf',      repo: 'mmf-agents',      agentKey: 'ambassador_boss',  brain: 'Gumloop + Logic',  trigger: 'Every 6 hours',    description: 'Tracks referral counts and auto-upgrades MedSoc reps.' },
];

const depts = ['All', 'Command', 'Product & Engineering', 'Customer Love', 'Study Engine', 'Marketing & Growth'];

export default function Agents() {
  const configured = isConfigured();
  const [selectedDept, setSelectedDept] = useState('All');
  const [selected, setSelected] = useState(AGENT_DEFS[0]);
  const [triggering, setTriggering] = useState({});
  const [triggerResults, setTriggerResults] = useState({});

  const { data: logs, refetch } = useLiveData(
    configured ? getAgentLogs : () => Promise.resolve(mockAgents.flatMap(a => a.logs.map(l => ({ ...l, agent_name: a.name, platform: a.platform, ran_at: new Date().toISOString() })))),
    30000
  );

  const getAgentStatus = (agentId) => {
    if (!logs || !configured) {
      const mock = mockAgents.find(a => a.id === agentId);
      return mock?.status || 'idle';
    }
    const agentLogs = logs.filter(l => l.agent_name?.toLowerCase().replace(/\s/g,'_') === agentId || l.agent_name === AGENT_DEFS.find(a=>a.id===agentId)?.name);
    if (!agentLogs.length) return 'idle';
    const latest = agentLogs[0];
    return latest.status === 'flagged' ? 'flagged' : latest.status === 'error' ? 'flagged' : 'running';
  };

  const getAgentLast = (agentId) => {
    if (!logs || !configured) return mockAgents.find(a=>a.id===agentId)?.lastRun || 'never';
    const agentLogs = logs.filter(l => l.agent_name === AGENT_DEFS.find(a=>a.id===agentId)?.name);
    if (!agentLogs.length) return 'never';
    return new Date(agentLogs[0].ran_at).toLocaleString('en-GB', {day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'});
  };

  const getAgentLogsList = (agentId) => {
    if (!logs || !configured) return mockAgents.find(a=>a.id===agentId)?.logs || [];
    return logs.filter(l => l.agent_name === AGENT_DEFS.find(a=>a.id===agentId)?.name).slice(0, 5);
  };

  const trigger = async (agent) => {
    if (!configured) return;
    setTriggering(p => ({ ...p, [agent.id]: true }));
    try {
      const result = await triggerAgent(agent.repo, agent.agentKey);
      setTriggerResults(p => ({ ...p, [agent.id]: { success: true, msg: result.message } }));
      setTimeout(() => refetch(), 3000);
    } catch (e) {
      setTriggerResults(p => ({ ...p, [agent.id]: { success: false, msg: e.message } }));
    } finally {
      setTriggering(p => ({ ...p, [agent.id]: false }));
    }
  };

  const filtered = AGENT_DEFS.filter(a => selectedDept === 'All' || a.dept === selectedDept);

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">AI Agents</div>
          <div className="page-subtitle">Control panel for all 11 agents across both businesses</div>
        </div>
        {!configured && <span style={{fontSize:'11px',color:'var(--text-3)',background:'var(--bg-2)',padding:'4px 12px',borderRadius:'20px',border:'1px solid var(--border)'}}>demo mode · connect Worker to trigger agents</span>}
      </div>

      <div className="dept-filter">
        {depts.map(d => (
          <div key={d} className={`dept-chip ${selectedDept===d?'active':''}`} onClick={() => setSelectedDept(d)}>{d}</div>
        ))}
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr 360px',gap:'20px',alignItems:'start'}}>
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          {filtered.map(agent => {
            const status = getAgentStatus(agent.id);
            const lastRun = getAgentLast(agent.id);
            const result = triggerResults[agent.id];
            return (
              <div key={agent.id} className={`agent-card ${selected?.id===agent.id?'selected':''}`} onClick={()=>setSelected(agent)}>
                <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:'8px'}}>
                  <div>
                    <div style={{display:'flex',gap:'8px',alignItems:'center',marginBottom:'4px'}}>
                      <div className="agent-name">{agent.name}</div>
                      <span className={`platform-tag ${agent.platform}`}>{agent.platform}</span>
                    </div>
                    <div style={{fontSize:'10px',color:'var(--text-3)'}}>{agent.dept}</div>
                  </div>
                  <div style={{display:'flex',gap:'8px',alignItems:'center'}}>
                    <span className={`status-badge ${status}`}>
                      <span style={{width:'5px',height:'5px',borderRadius:'50%',background:'currentColor',display:'inline-block'}}/>
                      {status}
                    </span>
                    <button
                      className="btn btn-approve"
                      style={{fontSize:'10px',padding:'4px 10px'}}
                      disabled={triggering[agent.id] || !configured}
                      onClick={e=>{e.stopPropagation();trigger(agent);}}
                    >
                      {triggering[agent.id] ? '⏳' : '▶ Run'}
                    </button>
                  </div>
                </div>
                <div className="agent-desc">{agent.description}</div>
                <div className="agent-meta">
                  <span>🧠 {agent.brain}</span>
                  <span>⚡ {agent.trigger}</span>
                  <span>🕐 {lastRun}</span>
                </div>
                {result && (
                  <div style={{marginTop:'8px',fontSize:'10px',color:result.success?'var(--green)':'var(--red)'}}>
                    {result.success ? `✓ ${result.msg}` : `✗ ${result.msg}`}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {selected && (
          <div className="agent-detail" style={{position:'sticky',top:'0'}}>
            <div className="agent-detail-header">
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'8px'}}>
                <span className={`status-badge ${getAgentStatus(selected.id)}`}>
                  <span style={{width:'5px',height:'5px',borderRadius:'50%',background:'currentColor',display:'inline-block'}}/>
                  {getAgentStatus(selected.id)}
                </span>
                <span className={`platform-tag ${selected.platform}`}>{selected.platform}</span>
              </div>
              <div style={{fontFamily:'var(--font-display)',fontSize:'18px',fontWeight:'800',color:'var(--text)',letterSpacing:'-0.02em',marginBottom:'4px'}}>{selected.name}</div>
              <div style={{fontSize:'11px',color:'var(--text-3)',marginBottom:'12px'}}>{selected.dept}</div>
              <button
                className="btn btn-approve"
                style={{width:'100%',justifyContent:'center'}}
                disabled={triggering[selected.id] || !configured}
                onClick={()=>trigger(selected)}
              >
                {triggering[selected.id] ? '⏳ Triggering on GitHub...' : `▶ Run ${selected.name} Now`}
              </button>
              {!configured && <div style={{fontSize:'10px',color:'var(--text-3)',textAlign:'center',marginTop:'6px'}}>Connect Worker to enable</div>}
            </div>
            <div className="agent-detail-body">
              <div style={{marginBottom:'16px'}}>
                <div style={{fontSize:'10px',letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--text-3)',marginBottom:'8px'}}>Config</div>
                {[['Brain',selected.brain],['Trigger',selected.trigger],['Last Run',getAgentLast(selected.id)],['GitHub Repo',selected.repo]].map(([k,v])=>(
                  <div key={k} style={{display:'flex',justifyContent:'space-between',padding:'5px 0',borderBottom:'1px solid var(--border)',fontSize:'11px'}}>
                    <span style={{color:'var(--text-3)'}}>{k}</span>
                    <span style={{color:'var(--text-2)',textAlign:'right',maxWidth:'60%'}}>{v}</span>
                  </div>
                ))}
              </div>
              <div>
                <div style={{fontSize:'10px',letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--text-3)',marginBottom:'8px'}}>Recent Logs</div>
                {getAgentLogsList(selected.id).map((log, i) => (
                  <div key={i} className="log-entry">
                    <span className="log-time">{log.time || new Date(log.ran_at||Date.now()).toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'})}</span>
                    <span className={`log-msg ${log.type||log.status==='error'?'error':log.status==='warn'||log.status==='flagged'?'warn':'ok'}`}>{log.msg||log.message}</span>
                  </div>
                ))}
                {getAgentLogsList(selected.id).length === 0 && (
                  <div style={{fontSize:'11px',color:'var(--text-3)',padding:'12px 0'}}>No logs yet. Run this agent to see output here.</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
